import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Trash2, Upload, MessageSquare, Loader2, Code } from 'lucide-react';
import {
  Button,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useAuth,
  kmsService,
} from '@aaas/shared';
import { useSpaceDetailQuery, useDocumentListQuery } from '../hooks/useSpaceListQuery';
import DocumentList from '../components/DocumentList';
import DocumentUploadModal from '../components/DocumentUploadModal';
import ProcessGoogleDriveSection from '../components/ProcessGoogleDriveSection';
import { useState } from 'react';

const SpaceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, currentWorkspace, idToken } = useAuth();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [showDeleteSpaceDialog, setShowDeleteSpaceDialog] = useState(false);
  const [isDeletingSpace, setIsDeletingSpace] = useState(false);

  // React Query for space details
  const {
    data: space,
    isLoading: loading,
    error,
  } = useSpaceDetailQuery({
    spaceId: id,
    idToken: idToken || undefined,
    enabled: !!id && !!user && !!currentWorkspace,
  });

  // React Query for documents (to check if space has documents)
  const { data: documentsResponse } = useDocumentListQuery({
    spaceId: id,
    idToken: idToken || undefined,
    enabled: !!id && !!idToken,
  });

  const documents = documentsResponse?.data ?? [];
  const hasReadyDocuments = documents.some((doc) => doc.upload_status === 'ready');

  const handleDeleteSpace = async () => {
    if (!space || !idToken) return;

    setIsDeletingSpace(true);
    try {
      await kmsService.deleteSpace(idToken, space.id);

      // Invalidate all queries that depend on spaces
      queryClient.invalidateQueries({ queryKey: ['kms-spaces'] });
      queryClient.invalidateQueries({ queryKey: ['access-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['slack-users'] });

      navigate('/kms/spaces');
    } catch (err: unknown) {
      // Handle space deletion errors gracefully
      const errorMessage =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Unknown error';
      alert('Failed to delete space: ' + errorMessage);
      setShowDeleteSpaceDialog(false);
    } finally {
      setIsDeletingSpace(false);
    }
  };

  const handleDocumentUpload = async (files: File[]) => {
    if (!space || !idToken) return;

    try {
      await kmsService.uploadDocuments(idToken, space.id, files);
      // DocumentList will auto-refresh via React Query polling
    } catch (err: unknown) {
      // Handle document upload errors gracefully
      const errorMessage =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Unknown error';
      alert('Failed to upload documents: ' + errorMessage);
    }
  };

  const handlePasteContent = async (content: string, title?: string) => {
    if (!space || !idToken) return;

    try {
      await kmsService.pasteTextContent(idToken, space.id, content, title);
      // DocumentList will auto-refresh via React Query polling
    } catch (err: unknown) {
      // Handle text document creation errors gracefully
      const errorMessage =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Unknown error';
      alert('Failed to create document: ' + errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load space'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="w-full p-6">
        <Alert>
          <AlertDescription>Space not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex gap-4">
          {/* Arrow container */}
          <div className="pt-1">
            <Button
              variant="ghost"
              onClick={() => navigate('/kms/spaces')}
              className="p-2"
              aria-label="Go back"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </div>

          {/* Main + Sub header container */}
          <div className="space-y-1">
            <h1 className="text-3xl font-bold">{space.name}</h1>
            {space.description && (
              <div className="text-sm text-gray-600">
                <span className="text-gray-600">{space.description}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions Section */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/kms/spaces/${space.id}/edit`)}
            aria-label="Edit space"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteSpaceDialog(true)}
            disabled={isDeletingSpace}
            aria-label={isDeletingSpace ? 'Deleting space' : 'Delete space'}
          >
            {isDeletingSpace ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Space Description */}
      {space.description && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-3">Description</h2>
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{space.description}</p>
        </div>
      )}

      {/* AI Features Section */}
      <div>
        <h2 className="text-lg font-semibold mb-3">AI Features</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Button
            onClick={() => navigate(`/kms/spaces/${space.id}/chat`)}
            className={`h-auto p-6 flex flex-col items-center gap-3 border-2 transition-all duration-200 rounded-lg ${
              hasReadyDocuments
                ? 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200 hover:shadow-md'
                : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
            }`}
            variant="outline"
            disabled={!hasReadyDocuments}
          >
            <MessageSquare className="w-8 h-8" />
            <div className="text-center">
              <div className="font-semibold text-base mb-1">Chat with Documents</div>
              <div className={`text-xs ${hasReadyDocuments ? 'text-green-600' : 'text-gray-400'}`}>
                {hasReadyDocuments
                  ? 'Ask questions and get AI-powered answers'
                  : 'Upload and wait for documents to be processed'}
              </div>
            </div>
          </Button>

          <Button
            onClick={() => navigate(`/kms/spaces/${space.id}/widgets`)}
            className="h-auto p-6 flex flex-col items-center gap-3 bg-purple-50 hover:bg-purple-100 text-purple-700 border-2 border-purple-200 transition-all duration-200 hover:shadow-md rounded-lg"
            variant="outline"
          >
            <Code className="w-8 h-8" />
            <div className="text-center">
              <div className="font-semibold text-base mb-1">Manage Widgets</div>
              <div className="text-xs text-purple-600">Associate widgets with this space</div>
            </div>
          </Button>
        </div>
      </div>

      {/* Google Drive Integration Section */}
      <div>
        <ProcessGoogleDriveSection spaceId={space.id} />
      </div>

      {/* Documents Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Documents</h2>
          <Button
            onClick={() => setUploadModalOpen(true)}
            className="transition-all duration-200 hover:shadow-md"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Documents
          </Button>
        </div>
        <DocumentList spaceId={space.id} />
      </div>

      <DocumentUploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleDocumentUpload}
        onPaste={handlePasteContent}
        spaceName={space.name}
      />

      {/* Delete Space Confirmation Dialog */}
      <AlertDialog open={showDeleteSpaceDialog} onOpenChange={setShowDeleteSpaceDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Space</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete space "{space.name}"? This action cannot be undone.
              All associated documents and data will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingSpace}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSpace}
              disabled={isDeletingSpace}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingSpace ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Space'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SpaceDetailPage;
